import {
  horizontalListSortingStrategy,
  SortableContext
} from "@dnd-kit/sortable";
import { Users, X } from "lucide-react";
import {
  useMemo,
  type FC,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode
} from "react";

import { SignalBadge } from "@/features/agentAttention/components/SignalBadge";
import { groupTone } from "@/features/agentAttention/signalTone";
import type { AttentionItem } from "@/features/agentAttention/types";
import { cn } from "@/lib/utils";

import {
  groupLabel,
  groupMark,
  isPageGroup,
  segmentLabel,
  type TabGroup as TabGroupModel
} from "../groups";
import { segmentDragId, useSortableTab } from "../hooks/useSortableTab";
import { shortTabName } from "../layout";
import { canCloseTab } from "../tabRoutes";
import type { AppTab } from "../types";
import { TabMark } from "./TabMark";

export type TabGroupMode = "open" | "lingering";

type Props = {
  group: TabGroupModel;
  /** `lingering`: the group you just left, dimmed until the pointer leaves the bar. */
  mode: TabGroupMode;
  activeId: string | null;
  /** Short client name when the bar is narrow. */
  compact: boolean;
  maxWidth: number;
  signalsFor: (tabId: string) => AttentionItem[];
  isRunning: (tabId: string) => boolean;
  onSelect: (tab: AppTab) => void;
  onClose: (tab: AppTab) => void;
  onKeep: (tab: AppTab) => void;
  /** Keyboard reordering (Ctrl+Shift+Left/Right) from a focused segment. */
  onMoveKey?: (e: KeyboardEvent, tab: AppTab) => void;
  /** Wraps the active segment so clicking it opens the page's details popover. */
  renderDetails?: (trigger: ReactElement) => ReactNode;
};

const tooltip = (tab: AppTab, label: string): string =>
  tab.kind === "page" ? tab.title : `${label} > ${segmentLabel(tab)}`;

/**
 * The badge and the close button share one slot, so hovering a badged
 * segment (badge out, close in) keeps its width. Without a badge the slot
 * takes no room until the segment is hovered or the close is focused.
 */
const TrailingSlot: FC<{
  tab: AppTab;
  signals: AttentionItem[];
  running: boolean;
  onClose: () => void;
}> = ({ tab, signals, running, onClose }) => {
  const hasBadge = signals.length > 0 || running;
  return (
    <span
      className={cn(
        "relative flex h-4 shrink-0 items-center justify-center",
        hasBadge
          ? "ml-1 min-w-3"
          : "w-0 transition-[width,margin-left] duration-150 ease-out group-focus-within/seg:ml-1 group-focus-within/seg:w-3 group-hover/seg:ml-1 group-hover/seg:w-3"
      )}
    >
      {hasBadge ? (
        <span className="flex transition-opacity duration-150 group-hover/seg:opacity-0">
          <SignalBadge items={signals} running={running} />
        </span>
      ) : null}
      <button
        type="button"
        aria-label={`Close ${
          tab.kind === "page" ? tab.title : segmentLabel(tab)
        }`}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="pointer-events-none absolute -inset-0.5 flex items-center justify-center rounded-xs text-fg-3 opacity-0 transition-opacity duration-150 group-hover/seg:pointer-events-auto group-hover/seg:opacity-100 hover:bg-fill-2 hover:text-fg focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:outline-none"
      >
        <X className="size-3" />
      </button>
    </span>
  );
};

/**
 * An open group: the client's avatar and name once, then one segment per open
 * tab (a case's workflow, Profile). Selecting a segment changes only its
 * fill, never its weight or width, and its badge stays until the signal is
 * handled. A page group is one segment with the page's icon and title.
 */
type SegmentProps = {
  tab: AppTab;
  label: string;
  page: boolean;
  on: boolean;
  signals: AttentionItem[];
  running: boolean;
  /** One tab can't be reordered within its group; dragging it moves the group. */
  sortable: boolean;
  onSelect: (tab: AppTab) => void;
  onClose: (tab: AppTab) => void;
  onKeep: (tab: AppTab) => void;
  onMoveKey?: (e: KeyboardEvent, tab: AppTab) => void;
  renderDetails?: (trigger: ReactElement) => ReactNode;
};

const Segment: FC<SegmentProps> = ({
  tab,
  label,
  page,
  on,
  signals,
  running,
  sortable,
  onSelect,
  onClose,
  onKeep,
  onMoveKey,
  renderDetails
}) => {
  const drag = useSortableTab<HTMLDivElement>(segmentDragId(tab.id), {
    disabled: !sortable
  });
  const withDetails = on && !page && Boolean(renderDetails);
  const closable = canCloseTab(tab.id);
  const name = page ? tab.title : segmentLabel(tab);
  const trigger = (
    <button
      type="button"
      title={withDetails ? undefined : tooltip(tab, label)}
      aria-label={
        withDetails
          ? `${tab.kind === "case" ? "Case" : "Client"} details: ${tooltip(
              tab,
              label
            )}`
          : tooltip(tab, label)
      }
      aria-current={on ? "page" : undefined}
      onClick={on ? undefined : () => onSelect(tab)}
      onDoubleClick={() => onKeep(tab)}
      onKeyDown={onMoveKey ? (e) => onMoveKey(e, tab) : undefined}
      className={cn(
        "flex min-w-0 items-center self-stretch text-left focus-visible:outline-none",
        page ? "gap-1.5" : "gap-1"
      )}
    >
      {page ? (
        <span data-flip-mark className="flex shrink-0">
          <TabMark tab={tab} className="size-3.5 text-fg-3" />
        </span>
      ) : null}
      {tab.kind === "client" ? <Users className="size-3 shrink-0" /> : null}
      <span className="min-w-0 truncate">{name}</span>
    </button>
  );
  const loud = on || groupTone(signals) === "red";
  return (
    <div
      ref={drag.ref}
      role="tab"
      aria-selected={on}
      data-tab-id={tab.id}
      data-active={on ? "true" : undefined}
      data-dragging={drag.isDragging ? "" : undefined}
      style={drag.style}
      onPointerDown={drag.onPointerDown}
      onAuxClick={(e) => {
        if (e.button === 1 && closable) onClose(tab);
      }}
      className={cn(
        "group/seg relative flex min-w-0 shrink items-center transition-colors duration-100 data-[dragging]:z-10 data-[dragging]:cursor-grabbing data-[dragging]:bg-elevated data-[dragging]:shadow-popover",
        page ? "h-full pr-2 text-sm" : "h-6 rounded-sm px-2 text-sm",
        !page &&
          (on
            ? "bg-fill-2 has-[[data-state=open]]:bg-fill-3"
            : "hover:bg-fill-1"),
        loud || page ? "text-fg" : "text-fg-3 hover:text-fg"
      )}
    >
      {withDetails && renderDetails ? renderDetails(trigger) : trigger}
      {closable ? (
        <TrailingSlot
          tab={tab}
          signals={signals}
          running={running}
          onClose={() => onClose(tab)}
        />
      ) : signals.length > 0 || running ? (
        <span className="ml-1 flex shrink-0">
          <SignalBadge items={signals} running={running} />
        </span>
      ) : null}
    </div>
  );
};

export const TabGroup: FC<Props> = ({
  group,
  mode,
  activeId,
  compact,
  maxWidth,
  signalsFor,
  isRunning,
  onSelect,
  onClose,
  onKeep,
  onMoveKey,
  renderDetails
}) => {
  const label = groupLabel(group);
  const page = isPageGroup(group);
  const drag = useSortableTab<HTMLDivElement>(group.key);
  const segmentIds = useMemo(
    () => group.tabs.map((t) => segmentDragId(t.id)),
    [group.tabs]
  );

  const segment = (tab: AppTab) => (
    <Segment
      key={tab.id}
      tab={tab}
      label={label}
      page={page}
      on={tab.id === activeId}
      signals={signalsFor(tab.id)}
      running={isRunning(tab.id)}
      sortable={group.tabs.length > 1}
      onSelect={onSelect}
      onClose={onClose}
      onKeep={onKeep}
      onMoveKey={onMoveKey}
      renderDetails={renderDetails}
    />
  );

  return (
    <div
      ref={drag.ref}
      data-flip-key={group.key}
      data-group-key={group.key}
      data-group-mode={mode}
      data-dragging={drag.isDragging ? "" : undefined}
      style={{ maxWidth, ...drag.style }}
      onPointerDown={drag.onPointerDown}
      className={cn(
        "relative flex h-7 min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-md transition-[background-color,opacity] duration-150 data-[dragging]:z-10 data-[dragging]:cursor-grabbing data-[dragging]:opacity-100 data-[dragging]:shadow-popover data-[resizing]:*:shrink-0",
        page ? "pr-0.5 pl-2" : "pr-0.5 pl-1.5",
        mode === "open" ? "bg-surface" : "bg-fill-1 opacity-60"
      )}
    >
      {page ? (
        segment(group.tabs[0])
      ) : (
        <>
          <span data-flip-mark className="flex shrink-0">
            <TabMark tab={groupMark(group)} />
          </span>
          <span
            title={label}
            className="mr-1.5 max-w-40 min-w-0 shrink-[2] truncate text-sm font-medium text-fg"
          >
            {compact ? shortTabName(label) : label}
          </span>
          <SortableContext
            items={segmentIds}
            strategy={horizontalListSortingStrategy}
          >
            {group.tabs.map(segment)}
          </SortableContext>
        </>
      )}
    </div>
  );
};
